import { Filter } from '@/lib/types';
import { cn, getRegionColor, getRegionDisplayName, getSectors } from '@/lib/utils';
import React from 'react';

interface FilterSidebarProps {
  isVisible: boolean;
  providers: string[];
  regions: string[];
  activeFilter: Filter;
  onFilterChange: (filter: Filter) => void;
  onClose: () => void;
  className?: string;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  isVisible,
  providers = [],
  regions = [],
  activeFilter,
  onFilterChange,
  onClose,
  className
}) => {
  // Handle provider selection - exclusive filter
  const handleProviderSelect = (provider: string) => {
    if (activeFilter.provider === provider) {
      onFilterChange({});
    } else {
      onFilterChange({ provider });
    }
  };

  // Handle sector selection - exclusive filter
  const handleSectorSelect = (sector: string) => {
    if (activeFilter.sector === sector) {
      onFilterChange({});
    } else {
      onFilterChange({ sector });
    }
  };

  // Handle region selection - exclusive filter
  const handleRegionSelect = (region: string) => {
    // If the same region is clicked again, clear the filter
    if (activeFilter.region === region) {
      onFilterChange({});
    } else {
      // Only set the region filter, clear any other filters
      onFilterChange({ region });
    }
  };

  // Clear all filters
  const clearFilters = () => {
    // Make sure we're fully resetting by creating a new empty object
    onFilterChange({});
  };

  // Safety check for filters that might be objects
  const safeProviders = providers.filter(item => typeof item === 'string');
  const safeRegions = regions.filter(item => typeof item === 'string');

  return (
    <div className={cn(
      "sidebar-fixed-width border-r border-[#333] overflow-y-auto h-full bg-black",
      isVisible ? "" : "hidden",
      className
    )}>
      <div className="terminal-section-header flex justify-between items-center text-xs py-1.5 px-3 bg-[#0a0a0a] border-b border-terminal">
        <span>Filter Settings</span>
        <button
          className="text-terminal hover:text-white text-base"
          onClick={onClose}
          aria-label="Close filters"
        >
          ×
        </button>
      </div>

      {/* Action Buttons */}
      <div className="px-3 py-2 border-b border-[#1a1a1a] flex flex-col gap-2 text-xs">
        <div className="flex justify-between items-center">
          <button
            className="text-[#4F94CD] hover:text-white font-semibold"
            onClick={clearFilters}
            aria-label="Reset filters"
          >
            RESET ALL
          </button>
          <span className="text-[#666]">F10=ADV</span>
        </div>
        <div className="flex justify-between items-center text-[#666]">
          <span>PGUP/PGDN=NAV</span>
          <span>ESC=CLEAR</span>
        </div>
      </div>

      {/* Filter by Sector */}
      <div className="terminal-section-header text-xs px-3 py-1.5 bg-[#0a0a0a]">
        <span role="heading" aria-level={2}>SECTORS</span>
      </div>
      <div className="filter-sidebar-list border-b border-[#1a1a1a]">
        {getSectors().map((sector, index) => (
          <button
            key={`sector-${index}`}
            className={cn(
              "filter-item text-xs",
              {
                "bg-[#1a1a1a]": activeFilter.sector === sector,
                "text-terminal": activeFilter.sector === sector,
                "text-[#ccc]": activeFilter.sector !== sector
              }
            )}
            onClick={() => handleSectorSelect(sector)}
            aria-pressed={activeFilter.sector === sector}
          >
            <span>{sector}</span>
            {activeFilter.sector === sector && <span className="ml-1" aria-hidden="true">►</span>}
          </button>
        ))}
      </div>

      {/* Filter by Provider */}
      <div className="terminal-section-header text-xs px-3 py-1.5 bg-[#0a0a0a]">
        <span role="heading" aria-level={2}>NEWS PROVIDERS</span>
      </div>
      <div className="filter-sidebar-list border-b border-[#1a1a1a]">
        {safeProviders.map((provider, index) => (
          <button
            key={`provider-${provider}-${index}`}
            className={cn(
              "filter-item text-xs",
              {
                "bg-[#1a1a1a]": activeFilter.provider === provider,
                "text-terminal": activeFilter.provider === provider,
                "text-[#ccc]": activeFilter.provider !== provider
              }
            )}
            onClick={() => handleProviderSelect(provider)}
            aria-pressed={activeFilter.provider === provider}
          >
            <span>{provider}</span>
            {activeFilter.provider === provider && <span className="ml-1" aria-hidden="true">►</span>}
          </button>
        ))}
      </div>

      {/* Filter by Region */}
      <div className="terminal-section-header text-xs px-3 py-1.5 bg-[#0a0a0a]">
        <span role="heading" aria-level={2}>REGIONS</span>
      </div>
      <div className="filter-sidebar-list">
        {safeRegions.map((region, index) => (
          <button
            key={`region-${region}-${index}`}
            className={cn(
              "filter-item text-xs flex items-center",
              {
                "bg-[#1a1a1a]": activeFilter.region === region,
                [activeFilter.region === region ? "text-terminal" : getRegionColor(region)]: true
              }
            )}
            onClick={() => handleRegionSelect(region)}
            aria-pressed={activeFilter.region === region}
          >
            <span>{getRegionDisplayName(region)}</span>
            {activeFilter.region === region && <span className="ml-1" aria-hidden="true">►</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

export default FilterSidebar;
